/* LicensingRenderer.js */

"use strict";

const uiCore = require("@gluenetworks/steve_ui_core");
const eventRegisterMixin = require("./utils/eventRegisterMixin");
const WarningsMessageComponent = require("./components/WarningsMessage/WarningsMessageComponent");
const AddLicenseComponent = require("./veil/addLicense/AddLicenseComponent");
const LicensesTableComponent = require("./components/DataTable/LicensesTableComponent");
const UsageTableComponent = require("./components/DataTable/UsageTableComponent");

const ButtonControl = uiCore.Control.Action.Button;

const LICENSES_INFO = (currentUTCDate) => `
    All dates below are displayed using the UTC time standard. Licenses start at midnight UTC and expire at 11.59pm UTC.
    <br />
    Current UTC date/time is ${currentUTCDate}.
`;
const ACTIVE_LICENSE_TITLE = "activated licenses";
const ACTIVE_LICENSE_NO_DATA = "no activated licenses";
const USAGE_TITLE = "current usage summary";
const USAGE_NO_DATA = "no license summary usage available";
const EXPIRED_TITLE = "expired licenses";
const EXPIRED_NO_DATA = "no expired licenses";
const DELETE_CONFIRMATION_MESSAGE =
    "Are you sure you want to permanently delete the selected license? This action cannot be undone and any devices added in this or subordinate organizations may result in exceeding your total number of licensed devices";
/**
 * @class LicensingRenderer
 *
 * @listens LicensingComponent#event:Organization
 * @listens LicensingComponent#event:SystemInfo
 * @listens LicensesTableComponent#event:Selected
 */
class LicensingRenderer extends eventRegisterMixin() {
    constructor(model) {
        super();
        /**
         * @type {LicensingComponent}
         */
        this._model = model;
        this._rootElem = this._createRootElement();

        this._currentUTCDate = new Date().toLocaleString( undefined, {
            timeZone: "UTC",
            year: "numeric",
            month: "2-digit",
            day:"2-digit",
            hour: "2-digit",
            minute: "2-digit"
        });

        this._copySystemInfoButton = new ButtonControl({
            label: "Copy info to clipboard",
            title: "Copy your system name and system token to the clipboard to send to Gluware to obtain a license\n",
            darkTheme: true
        });

        this._warnings = new WarningsMessageComponent();

        this._addButton = new ButtonControl({ label: "Add License", darkTheme: true });

        this._usageTable = new UsageTableComponent({
            title: USAGE_TITLE,
            noDataMessage: USAGE_NO_DATA
        });
        this._usageTable.setFeed(this._model.summaryFeed);

        const activeTableOptions = {
            title: ACTIVE_LICENSE_TITLE,
            noDataMessage: ACTIVE_LICENSE_NO_DATA,
            gridFiltering: {
                args: { expired: false },
                filterFn: (item, args) => Boolean(item.expired) === args.expired
            }
        };
        if (this._model.canAddLicense) {
            Object.assign(activeTableOptions, {
                buttons: [this._addButton],
                actions: {
                    delete: (licenseKey, item) => this._deleteItems(licenseKey, item)
                }
            });
        }

        this._activeTable = new LicensesTableComponent(activeTableOptions);
        this._activeTable.setOrgId(this._model.orgId);
        this._activeTable.setFeed(this._model.licenseFeed);

        this._expiredTable = new LicensesTableComponent({
            title: EXPIRED_TITLE,
            noDataMessage: EXPIRED_NO_DATA,
            gridFiltering: {
                args: { expired: true },
                filterFn: (item, args) => Boolean(item.expired) === args.expired
            }
        });
        this._expiredTable.setFeed(this._model.licenseFeed);

        this._registerListeners();
    }

    get rootElement() {
        return this.getRootElement();
    }

    get model() {
        return this.getModel();
    }

    /*******************************************/
    /* START PUBLIC COMPONENT RENDERER METHODS */

    /*******************************************/
    render() {

        if (this._renderProm === null) {
            return Promise.resolve();
        }
        this._renderProm = this._renderProm || this._render();
        return this._renderProm;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    getRootElement() {
        return this._rootElem;
    }

    getModel() {
        return this._model;
    }

    /********************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS */
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._registerListener(this._model, "Organization", this._onOrganizationChange.bind(this));
        this._registerListener(this._model, "SystemInfo", this._onSystemInfoChange.bind(this));
        this._registerListener(this._addButton, "UserClick", this._onAddLicenseClick.bind(this));
        this._registerListener(
            this._copySystemInfoButton,
            "UserClick",
            this._onCopyClick.bind(this)
        );

        this._registerSummaryFeedListeners();
    }

    _registerSummaryFeedListeners() {
        const setWarnings = (licenseStateEntities) =>
            this._warnings.setData(
                licenseStateEntities.map((licenseState) => licenseState.getProperties())
            );

        this._registerListener(this._model.summaryFeed, "ChangeFeedInitialized", (
            data,
            feed /*,reInit*/
        ) => {
            setWarnings(feed.getCacheAsArray());
        });
        this._registerListener(
            this._model.summaryFeed,
            "EntityAdded",
            (entity, properties, feed) => {
                setWarnings(feed.getCacheAsArray());
            }
        );
        this._registerListener(this._model.summaryFeed, "EntityRemoved", (entity, cause, feed) => {
            setWarnings(feed.getCacheAsArray());
        });
        this._registerListener(
            this._model.summaryFeed,
            "EntityPropertiesUpdated",
            (entity, patches, props, feed) => {
                setWarnings(feed.getCacheAsArray());
            }
        );
    }

    _unregisterListeners() {
        this._unregisterAllListeners();
    }

    _render() {
        this._rootElem.innerHTML = "";
        const root = document.createDocumentFragment();
        root.appendChild(this._createHeader());
        root.appendChild(this._createSystemInfoSection());
        root.appendChild(this._createSummarySection());
        root.appendChild(this._createWarningsSection());
        root.appendChild(this._createUsageSection());
        root.appendChild(this._createActiveSection());
        root.appendChild(this._createExpiredSection());

        return new Promise((resolve /*,reject*/) => {
            this._rootElem.appendChild(root);
            resolve();
        });
    }

    _shutdown() {
        this._unregisterListeners();
        this._rootElem.remove();
        this._rootElem = null;

        return Promise.all([
            this._addButton.shutdown(),
            this._usageTable.shutdown(),
            this._activeTable.shutdown(),
            this._expiredTable.shutdown()
        ])
            .then(() => {
                this._rootElem = null;
                this._model = null;
                this._shutdownProm = null;
                this._renderProm = null;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    _showAddNewLicenseVeil() {
        const component = new AddLicenseComponent({
            validate: this._model.previewLicense.bind(this._model),
            addLicense: this._model.addLicense.bind(this._model)
        });
        this._model.userFeedback
            .displayCustom(component, { notDismissable: false })
            .catch((error) => {
                if (error && error.userRejected === true) {
                    //the veil just has been closed
                    return;
                }
                return error && console.error(error);
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PRIVATE RENDERER METHODS */
    /*******************************************/

    _createRootElement() {
        const rootElement = document.createElement("div");
        rootElement.classList.add("licensing-gl");
        rootElement.component = this._model;
        return rootElement;
    }

    _createHeader() {
        const header = document.createElement("h1");
        header.innerText = "licenses";
        return header;
    }

    _createSystemInfoSection() {
        const section = document.createElement("section");
        section.classList.add("system-info");
        const systemInfoWrapper = this._renderSystemInfo(this._model.systemInfo);
        section.appendChild(systemInfoWrapper);
        return section;
    }

    _createSummarySection() {
        const section = document.createElement("section");
        section.classList.add("summary");
        const licenseInfo = document.createElement("p");
        licenseInfo.innerHTML = LICENSES_INFO(this._currentUTCDate);
        section.appendChild(licenseInfo);
        return section;
    }

    _createActiveSection() {
        const activeSection = document.createElement("section");
        activeSection.classList.add("data", "active-licenses");
        activeSection.appendChild(this._activeTable.rootElement);
        return activeSection;
    }

    _createUsageSection() {
        const usageSection = document.createElement("section");
        usageSection.classList.add("data", "usage-licenses");
        usageSection.appendChild(this._usageTable.rootElement);
        return usageSection;
    }

    _createExpiredSection() {
        const expiredSection = document.createElement("section");
        expiredSection.classList.add("data", "expired-licenses");
        expiredSection.appendChild(this._expiredTable.rootElement);
        this._expiredTable.setLoading(true);
        return expiredSection;
    }

    _createWarningsSection() {
        const warningsSection = document.createElement("section");
        warningsSection.classList.add("warnings");
        warningsSection.appendChild(this._warnings.rootElement);
        return warningsSection;
    }

    _renderSystemInfo({ systemName = "", token = "" } = {}) {
        this._systemInfoContainer = this._systemInfoContainer || document.createElement("div");
        this._systemInfoContainer.className = "system-info-wrapper";
        this._systemInfoContainer.innerHTML = "";

        const systemInfoElem = document.createElement("div");
        systemInfoElem.className = "system-info";

        const systemTokenContainer = (systemToken = "unknown") => {
            const token = document.createElement("p");
            token.innerText = `System Token: ${systemToken}`;
            return token;
        };
        const systemNameContainer = (systemName = "unknown") => {
            const name = document.createElement("p");
            name.innerText = `System Name: ${systemName}`;
            return name;
        };

        systemInfoElem.appendChild(systemNameContainer(systemName));
        systemInfoElem.appendChild(systemTokenContainer(token));
        this._systemInfoContainer.appendChild(systemInfoElem);

        if (systemName || token) {
            this._copySystemInfoButton.setValue(`${systemName}, ${token}`);
            const buttonEl =
                this._copySystemInfoButton.renderer &&
                this._copySystemInfoButton.renderer.rootElement;
            this._systemInfoContainer.appendChild(buttonEl);
        }

        return this._systemInfoContainer;
    }

    /*******************************************/
    /* END PRIVATE RENDERER METHODS  */
    /* START HANDLER METHODS */
    /*******************************************/
    _onOrganizationChange({ id }) {
        this._activeTable.setOrgId(id);
        this._expiredTable.setOrgId(id);
    }

    _onSystemInfoChange(systemInfo) {
        this._renderSystemInfo(systemInfo);
    }

    _onAddLicenseClick() {
        this._showAddNewLicenseVeil();
    }

    _onCopyClick() {
        copyToClipboard(this._copySystemInfoButton.getValue());
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _deleteItems(licenseKey, item) {
        this._activeTable.setLoading(true);
        this._model.userFeedback
            .displayConfirmation(DELETE_CONFIRMATION_MESSAGE, {
                title: "Delete",
                confirmDetails: {
                    text: "Confirm",
                    type: "destructive"
                },
                rejectDetails: {
                    text: "Cancel",
                    type: "text"
                }
            })
            .then(() => this._model.deleteLicense(item))
            .catch((error) => {
                this._activeTable.setLoading(false);
                return this.showError(error);
            });
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/

    showError(error) {
        if (!error || error.userRejected) {
            return;
        }
        return this._model.userFeedback.displayError({ error }).catch((error) => {
            if (error.userRejected) {
                return;
            }
            this._logger.warn({ error });
        });
    }
}
function copyToClipboard(value) {
    const fakeInput = document.createElement("input");

    fakeInput.setAttribute("value", value);
    fakeInput.readOnly = true;
    fakeInput.style.cssText = `
        position: fixed;
        bottom: 100%;
    `;
    document.body.appendChild(fakeInput);
    fakeInput.select();
    document.execCommand("copy");
    fakeInput.remove();
}
module.exports = LicensingRenderer;
